import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, CardBody, Col, FormFeedback, FormGroup, Input, Label, Row, Spinner } from 'reactstrap';
import Select from 'react-select';
import Moment from 'react-moment';

import * as appStoreActions from '../../actions/appStoreActions';
import AdvertAssets from './AdvertAssets';
import AdvertPreview from './AdvertPreview';
import AdvertStorePreview from './AdvertStorePreview';
import DeleteAdvertModal from './DeleteAdvertModal';
import RichText from './RichText';
import VisibilityChecklist from './VisibilityChecklist';
import { AppTile, TypePill, sourceLabel, typeLabel } from './rowParts';
import {
  addAsset,
  addTag,
  applyVisibilityLock,
  EMPTY_FORM,
  ERPS,
  facetOptions,
  formErrors,
  fromDto,
  isVideo,
  MAX_ASSETS,
  MAX_DESCRIPTION,
  moveAsset,
  planAssetBatch,
  placementOptions,
  prefillFromApp,
  tailorPlacement,
  removeAsset,
  removeTag,
  toRequest,
  TYPES
} from './advertModel';

// The picker's option carries the app; `label` is only what react-select searches on (name, source,
// type, key and the description, in the words the admin sees), the rendering is AppOptionLabel below.
const appOption = app => ({
  value: app.uuid,
  app,
  label: [app.name, sourceLabel(app.source), typeLabel(app.type), app.sourceKey, app.description]
    .filter(Boolean)
    .join(' ')
});

/** The briefing under a module's name: what the source says about it, else where it comes from and what it is. */
const briefing = app =>
  app.description || [sourceLabel(app.source), typeLabel(app.type), app.sourceKey].filter(Boolean).join(' · ');

/** Two lines in the menu: the module's name, then its briefing; the name alone once selected. */
const AppOptionLabel = ({ app, context }) => {
  if (context !== 'menu') return app.name;
  return (
    <div className="d-flex align-items-center">
      <AppTile name={app.name} imageUrl={app.imageUrl} size={28} />
      <div style={{ minWidth: 0 }}>
        <div className="fw-semibold text-truncate">
          {app.name}
          {app.active === false ? (
            <Badge color="secondary" className="ms-2">
              gone from its source
            </Badge>
          ) : null}
        </div>
        <div className="text-muted text-truncate" style={{ fontSize: 12 }}>
          {briefing(app)}
        </div>
      </div>
    </div>
  );
};

/** The linked module as a card under the picker — tile, name, briefing; nothing the option did not already say. */
const LinkedModuleCard = ({ app }) => (
  <div className="border rounded p-2 mt-2 d-flex align-items-center" style={{ backgroundColor: '#f8f9fa' }}>
    <AppTile name={app.name} imageUrl={app.imageUrl} size={44} />
    <div style={{ minWidth: 0 }}>
      <div className="fw-semibold text-truncate">
        {app.name}
        {app.active === false ? (
          <Badge color="secondary" className="ms-2">
            gone from its source
          </Badge>
        ) : null}
      </div>
      <div className="text-muted" style={{ fontSize: 13 }}>
        {briefing(app)}
      </div>
    </div>
  </div>
);

/**
 * Edit App Advert (UN-3248), serving both /adverts/new and /adverts/:uuid/edit.
 *
 * The screen never merges a mutation result into the form by hand: every mutation endpoint answers
 * with the full detail payload, the reducer stores it, and one effect re-derives the form from it.
 * That is what makes "saves and reloads end-to-end" (AC1) true rather than optimistic — the fields on
 * screen after a save are the persisted ones, including the flags the server's Not-Visible lock
 * cleared and the type it derived from the linked app.
 */
const AdvertForm = ({ advert, advertLoading, advertError, deleteError, app, appError, apps, appsLoaded, dispatch }) => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedApp = searchParams.get('appUuid');

  const [form, setForm] = useState(EMPTY_FORM);
  const [tagDraft, setTagDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingAssets, setUploadingAssets] = useState(false);
  const [assetNote, setAssetNote] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Field messages wait for the first Save or Publish, so a blank new form does not open covered in errors.
  const [attempted, setAttempted] = useState(false);
  // Filters for the module picker only (product feedback): they narrow the catalog offered for linking,
  // they never touch the advert's own type or placement.
  const [moduleTypeFilter, setModuleTypeFilter] = useState('');
  const [moduleSourceFilter, setModuleSourceFilter] = useState('');
  // Which detail payload the form was last derived from. Without it the sync effect would re-run on
  // every render that touched redux and throw away whatever the user had typed since.
  const syncedFrom = useRef(null);
  const prefilled = useRef(false);

  useEffect(() => {
    if (uuid) {
      dispatch(appStoreActions.getAdvert(uuid)).catch(() => {});
    } else {
      dispatch(appStoreActions.clearAdvert());
      setForm(EMPTY_FORM);
      setAttempted(false);
      syncedFrom.current = null;
      prefilled.current = false;
    }
  }, [dispatch, uuid]);

  // The module picker needs the catalog, and "Create advert" arrives with an app already chosen.
  useEffect(() => {
    if (!appsLoaded) {
      dispatch(appStoreActions.getApps()).catch(() => {});
    }
  }, [dispatch, appsLoaded]);

  useEffect(() => {
    // Only the advert this route names may drive the form: on /new the store may still hold the last
    // edited advert for one render before the clear lands, and a slow response for advert A can arrive
    // after the user has opened advert B. Either would copy the wrong advert into the form and a Save
    // would write it under this URL (reviews on #282).
    if (uuid && advert && advert.uuid === uuid && syncedFrom.current !== advert) {
      syncedFrom.current = advert;
      setForm(fromDto(advert));
    }
  }, [uuid, advert]);


  // New advert reached from All Apps ("Create advert"): fetch the app DETAIL — description and
  // imageUrl are pre-fill fields the All Apps row does not carry — and seed the form from it, so the
  // preview is not blank before the first save. Once only: a later refresh must not overwrite edits.
  useEffect(() => {
    if (uuid || !preselectedApp) return;
    dispatch(appStoreActions.getApp(preselectedApp)).catch(() => {});
  }, [dispatch, uuid, preselectedApp]);

  useEffect(() => {
    if (uuid || prefilled.current || !preselectedApp) return;
    if (app && app.uuid === preselectedApp) {
      prefilled.current = true;
      setForm(prefillFromApp(app));
    }
  }, [uuid, preselectedApp, app]);

  const set = (field, value) => setForm(current => applyVisibilityLock({ ...current, [field]: value }));

  const linked = !!form.appUuid;
  const persistedStatus = advert ? advert.status : 'DRAFT';
  const published = persistedStatus === 'PUBLISHED';
  // The catalog list and the advert detail load independently; while the list is still on its way the
  // advert's own allowedPlacements / nativePlacement (for its linked app) stand in, so the select never
  // flips from five options to two after the fact.
  const linkedApp =
    apps.find(a => a.uuid === form.appUuid) ||
    (advert && advert.appUuid && advert.appUuid === form.appUuid && advert.allowedPlacements
      ? { uuid: advert.appUuid, allowedPlacements: advert.allowedPlacements, nativePlacement: advert.nativePlacement }
      : undefined);
  const busy = saving || uploading || uploadingAssets;
  const errors = formErrors(form);
  const shown = attempted ? errors : {};
  // Each filter offers what the OTHER one has left standing (UN-3372 item 8). Both lists were drawn from the
  // whole catalog independently, so picking a type kept offering every source in existence — including sources
  // with no app of that type, which selected to an empty picker and looked like a broken catalog. Narrowing each
  // list against the other's current selection means every option still returns at least one module, and a
  // selection that its counterpart has just excluded is dropped below rather than left as a dead value.
  //
  // Memoised so the two effects below depend on what they appear to depend on. facetOptions builds a fresh
  // array per call, so an unmemoised list is a new reference every render and both effects run every render —
  // harmless today because the setState is guarded, and a render loop the moment someone adds an unguarded one
  // (review on #284).
  const moduleTypes = useMemo(
    () => facetOptions(apps, 'type', 'source', moduleSourceFilter),
    [apps, moduleSourceFilter]
  );
  const moduleSources = useMemo(
    () => facetOptions(apps, 'source', 'type', moduleTypeFilter),
    [apps, moduleTypeFilter]
  );
  const filteredApps = apps.filter(
    a => (!moduleTypeFilter || a.type === moduleTypeFilter) && (!moduleSourceFilter || a.source === moduleSourceFilter)
  );

  // A filter whose value the other one has just excluded is cleared, not left selected: it would otherwise sit
  // in the select as a value with no <option>, which renders as blank and filters the picker to nothing.
  //
  // Guarded on a non-empty catalog, because an empty one excludes everything. apps is [] before the catalog
  // loads and again for the moment a refetch is in flight, and without this an in-flight reload silently threw
  // away a filter the admin had chosen (review on #284).
  useEffect(() => {
    if (apps.length && moduleTypeFilter && !moduleTypes.includes(moduleTypeFilter)) setModuleTypeFilter('');
  }, [apps, moduleTypeFilter, moduleTypes]);
  useEffect(() => {
    if (apps.length && moduleSourceFilter && !moduleSources.includes(moduleSourceFilter)) setModuleSourceFilter('');
  }, [apps, moduleSourceFilter, moduleSources]);

  // The placement follows the linked module: once the catalog (or the advert's own allowedPlacements)
  // is known, a stored slot the app does not allow snaps to the app's native one, so what the select
  // shows is what Save sends. Before this only the picker's onChange tailored it, so a pre-rule advert
  // opened for editing displayed the first option while the form still held the old value.
  useEffect(() => {
    if (!linkedApp) return;
    setForm(current => {
      const tailored = tailorPlacement(current.placement, linkedApp);
      return tailored === current.placement ? current : { ...current, placement: tailored };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedApp && linkedApp.uuid, linkedApp && (linkedApp.allowedPlacements || []).join(',')]);

  const commitTag = () => {
    setForm(current => ({ ...current, tags: addTag(current.tags, tagDraft) }));
    setTagDraft('');
  };

  const handleImage = event => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    setUploading(true);
    dispatch(appStoreActions.uploadAdvertImage(file))
      .then(image => set('imageUrl', image.url))
      .catch(() => {})
      .finally(() => {
        setUploading(false);
        // Clear the input so re-picking the same file fires change again after a failed upload.
        event.target.value = '';
      });
  };

  /**
   * Gallery uploads (UN-3334): the picker allows several files; they upload one after another and each lands
   * in the form as it returns. The two rules the server also enforces — one video, at most MAX_ASSETS — are
   * applied to the batch up front, so what is skipped is said before anything uploads.
   */
  const handleAssets = event => {
    const picked = Array.from(event.target.files || []);
    const input = event.target;
    if (!picked.length) return;
    const { accepted, notes } = planAssetBatch(form.assets, picked);
    setAssetNote(notes.join(' '));
    if (!accepted.length) {
      input.value = '';
      return;
    }
    setUploadingAssets(true);
    accepted
      .reduce(
        (chain, file) =>
          // One file's failure must not strand the rest of the batch (review on #283): each upload catches its
          // own error, says which file it was, and the chain carries on with the next.
          chain.then(() =>
            dispatch(appStoreActions.uploadAdvertAsset(file))
              .then(asset => setForm(current => ({ ...current, assets: addAsset(current.assets, asset) })))
              .catch(() => setAssetNote(current => `${current} ${file.name} failed to upload.`.trim()))
          ),
        Promise.resolve()
      )
      .finally(() => {
        setUploadingAssets(false);
        input.value = '';
      });
  };

  /**
   * Save order is load-bearing on an existing advert: fields, then the link change, then the
   * lifecycle move. Publish refuses an advert with no linked app, so a save that both links an app
   * and publishes has to link first; and unpublishing before an unlink keeps the listing from
   * briefly showing an advert whose app has gone.
   */
  const persist = ({ publish = false, unpublish = false } = {}) => {
    setAttempted(true);
    if (Object.keys(errors).length) return Promise.resolve();
    setSaving(true);
    // Order matters. The server locks the type while an advert is linked, so clearing the link and
    // picking a new type in one save has to UNLINK first, then update; a new link goes after the
    // update, because the update may still carry the old type. (Review on #282.)
    const persisted = advert ? advert.appUuid || null : null;
    const unlinkFirst = target =>
      persisted && form.appUuid !== persisted ? dispatch(appStoreActions.unlinkAdvert(target)) : Promise.resolve();
    const linkAfter = target =>
      form.appUuid && form.appUuid !== persisted
        ? dispatch(appStoreActions.linkAdvert(target, form.appUuid))
        : Promise.resolve();

    const flow = uuid
      ? unlinkFirst(uuid)
          .then(() => dispatch(appStoreActions.updateAdvert(uuid, toRequest(form))))
          .then(() => linkAfter(uuid))
          .then(() => {
            if (publish) return dispatch(appStoreActions.publishAdvert(uuid));
            if (unpublish) return dispatch(appStoreActions.unpublishAdvert(uuid));
            return null;
          })
      : dispatch(appStoreActions.createAdvert(toRequest(form, { create: true }))).then(created => {
          // Move to the edit URL as soon as the row exists. If the publish below fails (an unlinked
          // advert cannot publish), the draft is already saved — staying on /new would create a
          // second advert on the next attempt.
          navigate(`/manage-apps/adverts/${created.uuid}/edit`, { replace: true });
          return publish ? dispatch(appStoreActions.publishAdvert(created.uuid)) : null;
        });

    // A save that worked ends on the listing, with the new advert in it (UN-3372 item 10); a save that failed
    // stays on the form with its values and its error, which is why this hangs off .then and not .finally.
    //
    // It does NOT replace the navigate above. That one runs the moment the row exists, so a publish that then
    // fails leaves the browser on the saved draft's edit URL rather than back on /new, where saving again would
    // create a second advert. This is the end of a flow that entirely succeeded; that one is the safety net for
    // a flow that got halfway.
    return flow
      .then(() => navigate('/manage-apps/adverts'))
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  const handleDelete = () =>
    dispatch(appStoreActions.deleteAdvert(uuid))
      .then(() => {
        setConfirmDelete(false);
        navigate('/manage-apps/adverts');
      })
      .catch(() => {}); // the modal stays open and shows deleteError; closing it hid the failure

  if (uuid && advertLoading) {
    return (
      <div className="text-center py-5 text-muted">
        <Spinner size="sm" className="me-2" />
        Loading the advert&hellip;
      </div>
    );
  }

  if (uuid && !advert) {
    // A failed load used to fall through to an editable EMPTY_FORM with Save enabled: saving it would have
    // PUT blank fields, cleared the image and the tags, and set every flag false on an advert that exists
    // server-side (reviews on #282). Show the failure and a way back or a retry; never the form.
    return (
      <div>
        <Row className="mb-3">
          <Col>
            <h4 className="mb-0">Edit App Advert</h4>
          </Col>
          <Col className="text-end">
            <Button color="light" className="border" onClick={() => navigate('/manage-apps/adverts')}>
              Back
            </Button>
          </Col>
        </Row>
        <Alert color="danger">
          {advertError || 'The advert could not be loaded.'}
          <Button color="link" className="ps-2" onClick={() => dispatch(appStoreActions.getAdvert(uuid)).catch(() => {})}>
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <h4 className="mb-0">{uuid ? 'Edit App Advert' : 'New App Advert'}</h4>
        </Col>
        <Col className="text-end">
          <Button color="light" className="border me-2" onClick={() => setPreviewOpen(true)}>
            <i className="icon-eye me-1" />
            Preview
          </Button>
          <Button color="light" className="border" onClick={() => navigate('/manage-apps/adverts')}>
            Back
          </Button>
        </Col>
      </Row>

      {advertError ? <Alert color="danger">{advertError}</Alert> : null}
      {!uuid && preselectedApp && appError ? (
        <Alert color="warning">The catalog app to pre-fill from could not be loaded: {appError}</Alert>
      ) : null}

      <Row>
        <Col lg="8">
          <FormGroup>
            <Label for="advert-name">
              App Name <span className="text-danger">*</span>
            </Label>
            <Input
              id="advert-name"
              value={form.name}
              maxLength={255}
              disabled={busy}
              invalid={!!shown.name}
              onChange={e => set('name', e.target.value)}
            />
            <FormFeedback>{shown.name}</FormFeedback>
          </FormGroup>

          <FormGroup>
            <Label for="advert-description">Description</Label>
            <Input
              id="advert-description"
              type="textarea"
              rows="2"
              maxLength={MAX_DESCRIPTION}
              value={form.description}
              disabled={busy}
              invalid={!!shown.description}
              onChange={e => set('description', e.target.value)}
            />
            <FormFeedback>{shown.description}</FormFeedback>
            <div className={`mt-1 ${errors.description ? 'text-danger' : 'text-muted'}`} style={{ fontSize: 12 }}>
              {form.description.length}/{MAX_DESCRIPTION} — the summary shown on the App Store card.
            </div>
          </FormGroup>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="module-type-filter">Filter modules by type</Label>
                <Input
                  id="module-type-filter"
                  type="select"
                  value={moduleTypeFilter}
                  disabled={busy || published}
                  onChange={e => setModuleTypeFilter(e.target.value)}
                >
                  <option value="">All types</option>
                  {moduleTypes.map(t => (
                    <option key={t} value={t}>
                      {typeLabel(t)}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="module-source-filter">Filter modules by source</Label>
                <Input
                  id="module-source-filter"
                  type="select"
                  value={moduleSourceFilter}
                  disabled={busy || published}
                  onChange={e => setModuleSourceFilter(e.target.value)}
                >
                  <option value="">All sources</option>
                  {moduleSources.map(src => (
                    <option key={src} value={src}>
                      {sourceLabel(src)}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label>
              Linked module <span className="text-danger">*</span>
            </Label>
            <Select
              isClearable
              // The server refuses link and unlink on a PUBLISHED advert (app-advert-link-published):
              // the listing's app cannot change under the stores that already installed it. Disabled
              // rather than left enabled to fail on save with a 422 the user cannot act on.
              isDisabled={busy || !appsLoaded || published}
              placeholder={appsLoaded ? 'Search the catalog by app name, source or key…' : 'Loading the catalog…'}
              noOptionsMessage={() =>
                filteredApps.length === 0 && apps.length > 0
                  ? 'Nothing left after the filters above — widen them.'
                  : 'No catalog app matches that search.'
              }
              formatOptionLabel={(option, { context }) => <AppOptionLabel app={option.app} context={context} />}
              options={filteredApps.map(appOption)}
              value={linkedApp && linkedApp.name ? appOption(linkedApp) : null}
              onChange={selected => {
                const chosen = selected ? apps.find(a => a.uuid === selected.value) : null;
                setForm(current => ({
                  ...current,
                  appUuid: selected ? selected.value : null,
                  // Type follows the app while linked; clearing the link leaves the last type in place
                  // as the starting point for an explicit choice.
                  type: chosen ? chosen.type || current.type : current.type,
                  // So does the placement: a carrier cannot sit under Payment Provider, so the slot
                  // snaps to the app's native one when the current choice is no longer offered.
                  placement: chosen ? tailorPlacement(current.placement, chosen) : current.placement
                }));
              }}
            />
            {linkedApp && linkedApp.name ? <LinkedModuleCard app={linkedApp} /> : null}
            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
              {published
                ? 'Unpublish the advert to change or remove the linked app.'
                : linked
                  ? 'The advert publishes against this catalog app. Install counts and installed sites come from it.'
                  : 'Required to publish. A draft can be saved without one.'}
            </div>
          </FormGroup>

          <FormGroup>
            <Label>Full Description</Label>
            <RichText
              value={form.fullDescription}
              disabled={busy}
              onChange={html => set('fullDescription', html)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Screenshots &amp; preview</Label>
            <AdvertAssets
              assets={form.assets}
              disabled={busy}
              uploading={uploadingAssets}
              onAdd={handleAssets}
              onMove={(from, to) => setForm(current => ({ ...current, assets: moveAsset(current.assets, from, to) }))}
              onRemove={index => setForm(current => ({ ...current, assets: removeAsset(current.assets, index) }))}
            />
            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
              Up to {MAX_ASSETS}: PNG, JPEG, GIF or WebP screenshots (5 MB each) and one MP4 or WebM preview video (50 MB).
              Shown in this order in the store&rsquo;s detail dialog; hover a tile to reorder or remove it.
              {assetNote ? <span className="text-warning ms-1">{assetNote}</span> : null}
            </div>
          </FormGroup>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="advert-placement">Store Admin Placement</Label>
                <Input
                  id="advert-placement"
                  type="select"
                  value={form.placement}
                  disabled={busy}
                  onChange={e => set('placement', e.target.value)}
                >
                  {placementOptions(linkedApp).map(p => (
                    <option key={p.value} value={p.value}>
                      {p.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="advert-type">Type</Label>
                {linked ? (
                  <div>
                    <div className="mt-1">
                      <TypePill type={form.type} />
                    </div>
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      Derived from the linked app — unlink to change it.
                    </div>
                  </div>
                ) : (
                  <Input
                    id="advert-type"
                    type="select"
                    value={form.type}
                    disabled={busy}
                    onChange={e => set('type', e.target.value)}
                  >
                    <option value="">Select a type…</option>
                    {TYPES.map(t => (
                      <option key={t.value} value={t.value}>
                        {t.name}
                      </option>
                    ))}
                  </Input>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="advert-price">Monthly Price</Label>
                <Input
                  id="advert-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.monthlyPrice}
                  disabled={busy}
                  onChange={e => set('monthlyPrice', e.target.value)}
                />
                <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                  0 shows as Free. Leaving it blank keeps the saved price.
                </div>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>ERP Compatibility</Label>
                <Select
                  isMulti
                  isDisabled={busy}
                  options={ERPS}
                  value={ERPS.filter(e => form.erpCompatibility.includes(e.value))}
                  onChange={selected => set('erpCompatibility', (selected || []).map(s => s.value))}
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label>Tags</Label>
            <div className="mb-2">
              {form.tags.map(tag => (
                <Badge key={tag} color="light" className="text-dark border me-1">
                  {tag}
                  <Button
                    close
                    aria-label={`Remove ${tag}`}
                    className="ms-1"
                    style={{ fontSize: 12, lineHeight: 1 }}
                    disabled={busy}
                    onClick={() => setForm(current => ({ ...current, tags: removeTag(current.tags, tag) }))}
                  />
                </Badge>
              ))}
              {form.tags.length === 0 ? <span className="text-muted">No tags yet.</span> : null}
            </div>
            <Input
              value={tagDraft}
              placeholder="Type a tag and press Enter"
              disabled={busy}
              onChange={e => setTagDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ',') {
                  // Enter would submit the surrounding form and reload the page.
                  e.preventDefault();
                  commitTag();
                }
              }}
              onBlur={commitTag}
            />
            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
              Case-insensitive: adding &ldquo;CRM&rdquo; when &ldquo;crm&rdquo; is already there changes nothing.
            </div>
          </FormGroup>

        </Col>

        <Col lg="4">
          <AdvertPreview
            form={form}
            uploading={uploading}
            disabled={busy}
            onUpload={handleImage}
            onRemove={() => set('imageUrl', '')}
          />

          <Card className="mb-3">
            <CardBody>
              <div className="text-muted text-uppercase mb-2" style={{ fontSize: 11, letterSpacing: '.05em' }}>
                Status
              </div>
              {published ? (
                <Badge color="success">Published</Badge>
              ) : (
                <Badge color="secondary">{uuid ? 'Draft' : 'Not saved yet'}</Badge>
              )}

              <div
                className="text-muted text-uppercase mt-3 mb-2"
                style={{ fontSize: 11, letterSpacing: '.05em' }}
              >
                Visibility
              </div>
              <VisibilityChecklist form={form} disabled={busy} onChange={set} />
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <div className="text-muted text-uppercase mb-2" style={{ fontSize: 11, letterSpacing: '.05em' }}>
                Audit
              </div>
              <FormGroup>
                <Label for="advert-author">Author</Label>
                <Input
                  id="advert-author"
                  value={form.author}
                  maxLength={255}
                  disabled={busy}
                  onChange={e => set('author', e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label for="advert-version">Published Version</Label>
                <Input
                  id="advert-version"
                  value={form.publishedVersion}
                  maxLength={64}
                  disabled={busy}
                  onChange={e => set('publishedVersion', e.target.value)}
                />
              </FormGroup>
              <div style={{ fontSize: 12 }}>
                <div className="text-muted">Last update</div>
                {advert && advert.modifiedDate ? (
                  <Moment format="YYYY-MM-DD HH:mm">{advert.modifiedDate}</Moment>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <hr />
      <Row>
        <Col>
          {uuid ? (
            <Button color="danger" outline disabled={busy} onClick={() => setConfirmDelete(true)}>
              <i className="icon-trash me-1" />
              Delete advert
            </Button>
          ) : null}
        </Col>
        <Col className="text-end">
          {published ? (
            <Button color="light" className="border me-2" disabled={busy} onClick={() => persist({ unpublish: true })}>
              Unpublish
            </Button>
          ) : null}
          <Button color="light" className="border me-2" disabled={busy} onClick={() => persist()}>
            {saving ? <Spinner size="sm" className="me-1" /> : null}
            {published ? 'Save' : 'Save as Draft'}
          </Button>
          {published ? null : (
            <Button
              color="dark"
              // The server refuses to publish an unlinked advert (app-advert-publish-requires-link):
              // the consumer listing joins through the app. Disabled with the reason rather than
              // left to fail on save.
              disabled={busy || !linked}
              title={linked ? undefined : 'Link a catalog app before publishing'}
              onClick={() => persist({ publish: true })}
            >
              Publish
            </Button>
          )}
        </Col>
      </Row>

      <AdvertStorePreview
        isOpen={previewOpen}
        form={form}
        linkedApp={linkedApp}
        advert={advert}
        onClose={() => setPreviewOpen(false)}
      />

      <DeleteAdvertModal
        advert={confirmDelete ? { name: form.name } : null}
        error={deleteError}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
};

export default connect(state => ({
  advert: state.appStore.advert,
  advertLoading: state.appStore.advertLoading,
  advertError: state.appStore.advertError,
  deleteError: state.appStore.deleteError,
  app: state.appStore.app,
  appError: state.appStore.appError,
  apps: state.appStore.apps,
  appsLoaded: state.appStore.appsLoaded
}))(AdvertForm);
